import { useState, useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug),
    [activeSlug]
  );

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 穿搭换装指南`,
    description: currentCategory.description,
    url: `https://yourdomain.com/category/${activeSlug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '穿搭换装指南',
      url: 'https://yourdomain.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Category Hero */}
          <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
            <img
              src={currentCategory.image}
              alt={currentCategory.name}
              className="absolute inset-0 w-full h-full object-cover object-top"
              title={currentCategory.name}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
            <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-primary-500/90 mb-4">
                <i className={`${currentCategory.icon} text-xl md:text-2xl text-background-50`}></i>
              </div>
              <h1 className="font-heading text-xl md:text-3xl text-background-50">
                {currentCategory.name}
              </h1>
              <p className="mt-3 text-sm md:text-base text-background-100/90 max-w-xl text-center">
                {currentCategory.description}
              </p>
            </div>
          </section>

          <Breadcrumb items={breadcrumbItems} />

          {/* Category Tabs */}
          <section className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Articles Grid */}
          <section className="w-full px-4 md:px-6 py-6 md:py-10">
            <div className="max-w-7xl mx-auto">
              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}